import React from 'react';
import { IconCloseLight } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { cx } from '@emotion/css';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Typography } from '@common/styles';

interface ArrowPosition {
  direction: 'up' | 'down';
  position: 'left' | 'center' | 'right';
}

interface Props {
  text: string;
  arrowPosition: ArrowPosition;
  display?: boolean;
  animate?: boolean;
  top?: string;
  left?: string;
  right?: string;
  tipLeft?: string;
  zIndex?: number;
  whiteSpace?: 'normal' | 'nowrap' | 'pre' | 'pre-wrap' | 'pre-line';
  onClick?: () => void;
  onClose?: () => void;
}

export const Tooltip: React.FC<React.PropsWithChildren<Props>> = ({
  text,
  arrowPosition,
  display,
  animate = false,
  top = '0px',
  left = '50%',
  right,
  tipLeft,
  zIndex = 205,
  whiteSpace = 'nowrap',
  onClick,
  onClose,
}) => {
  const as = onClick ? 'button' : 'div';
  const tooltip_type_checked = !!onClose;
  const disabledPointerEvent = !onClose && !onClick;

  const handleTooltipClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onClick?.();
  };

  return (
    <Container
      as={as}
      className={cx(Typography.BODY_15_MEDIUM, display ? 'show' : '')}
      has_checked={tooltip_type_checked}
      top={top}
      left={left}
      right={right}
      zIndex={zIndex}
      animate={animate}
      whiteSpace={whiteSpace}
      disabledPointerEvent={disabledPointerEvent}
      onClick={handleTooltipClick}
    >
      {text}
      <TipIcon direction={arrowPosition.direction} position={arrowPosition.position} tipLeft={tipLeft} />
      {onClose && <IconCloseLight size={20} v3Color='staticWhite' onClick={onClose} />}
    </Container>
  );
};

const getPositionStyle = (position: 'left' | 'center' | 'right') => {
  switch (position) {
    case 'left':
      return 'left: 11px;';
    case 'center':
      return 'left: calc(50% - 12px);';
    case 'right':
      return 'right: 28px;';
    default:
      return null;
  }
};

const checked_style = css`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 5px;
  padding: 8px 7px 8px 12px;
`;

const Container = styled.div<{
  disabledPointerEvent: boolean;
  has_checked?: boolean;
  animate: boolean;
  top: string;
  left: string;
  right?: string;
  zIndex: number;
  whiteSpace: string;
}>`
  padding: 8px 12px;
  background: ${vars.v3Color.purple400};
  border-radius: 2px;
  color: ${vars.v3Color.staticWhite};
  position: absolute;
  top: ${({ top }) => top};
  left: ${({ left, right }) => !right && left};
  ${({ left, right }) => !right && (left === '50%' || left === '62%') && 'transform: translateX(-50%)'};
  right: ${({ right }) => right};
  z-index: ${({ zIndex }) => zIndex};
  white-space: ${({ whiteSpace }) => whiteSpace};
  font-weight: 500;
  font-size: 15px;
  line-height: 140%;

  ${({ disabledPointerEvent }) => disabledPointerEvent && 'pointer-events: none;'}

  ${({ animate }) =>
    animate &&
    `
      opacity: 0;
      transition: opacity 0.1s linear;
    
      &.show {
        opacity: 1;
        transition: opacity 0.2s linear;
      }
  `}

  ${({ has_checked }) => has_checked && checked_style}
`;

const DirectionUpStyle = css`
  top: -20px;
  transform: rotateX(180deg);
`;

const TipIcon = styled.div<{ direction: 'up' | 'down'; position: 'left' | 'center' | 'right'; tipLeft?: string }>`
  position: absolute;
  ${({ direction }) => (direction === 'up' ? DirectionUpStyle : 'bottom: -20px')};
  ${({ position }) => getPositionStyle(position)};
  ${({ tipLeft }) => tipLeft && `left: ${tipLeft};`};
  border-width: 12px;
  border-style: solid;
  border-color: ${vars.v3Color.purple400} transparent transparent transparent;
`;
